/**
 * CachrManager.ts - Platform-agnostic cache management with LRU eviction
 * 
 * CHANGE HISTORY:
 * - 251125_2220 danielbeer: Add TypeScript types
 * - 251019_1210 [022bb45] danielbeer: fix(cachr): make set() async to support HybridStorageAdapter
 * - 251019_1205 [4bbdb54] danielbeer: fix(cachr): use localStorage for preferences/metadata with async storage
 * - 251019_1124 [961bc6d] danielbeer: fix(cachr): add missing calculateSize method to CachrManager
 * - 251019_1122 [0b22d83] danielbeer: feat(cachr): add IndexedDB and hybrid storage adapters
 * - 251019_1118 [b49e5b5] danielbeer: fix(cachr): prevent infinite recursion on quota exceeded
 * - 251019_1103 [ffe436f] danielbeer: fix(cachr): auto-prune on QuotaExceededError
 * - 251019_1100 [b24c704] danielbeer: fix(cachr): remove incompatible react-hooks testing library
 * - 251019_1000 [0deebd6] danielbeer: refactor: Extract CacheManager to @opcentral/cachr package
 */
import type { CachrOptions, CacheStats, SetResult, CacheMetadata, StorageAdapter as IStorageAdapter } from './types';

const DEFAULT_CACHE_LIMIT = 50 * 1024 * 1024; // 50MB default
const WARNING_THRESHOLD = 0.8; // Warn at 80% capacity
const PRUNE_TARGET = 0.7; // Prune down to 70% when limit reached

/**
 * Storage adapter base class for platform-agnostic storage
 */
abstract class StorageAdapter implements IStorageAdapter {
  abstract getItem(key: string): string | null | Promise<string | null>;
  abstract setItem(key: string, value: string): void | Promise<void>;
  abstract removeItem(key: string): void | Promise<void>;
}

/**
 * LocalStorage adapter for browser environments
 */
export class LocalStorageAdapter extends StorageAdapter {
  getItem(key: string): string | null {
    return localStorage.getItem(key);
  }

  setItem(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  removeItem(key: string): void {
    localStorage.removeItem(key);
  }
}

/**
 * Memory adapter for Node.js or testing environments
 */
export class MemoryStorageAdapter extends StorageAdapter {
  private storage: Map<string, string>;

  constructor() {
    super();
    this.storage = new Map();
  }

  getItem(key: string): string | null {
    return this.storage.get(key) || null;
  }

  setItem(key: string, value: string): void {
    this.storage.set(key, value);
  }

  removeItem(key: string): void {
    this.storage.delete(key);
  }

  clear(): void {
    this.storage.clear();
  }
}

/**
 * Core CachrManager class - platform-agnostic
 */
export class CachrManager {
  private actr: any;
  private prefix: string;
  private metaKey: string;
  private prefsKey: string;
  private storage: IStorageAdapter;
  private isAsync: boolean;
  private metaStorage: LocalStorageAdapter;
  private preferences: any;
  private metadata!: CacheMetadata;

  constructor(options: CachrOptions = {}) {
    this.actr = options.actr;
    this.prefix = options.prefix || 'cachr_';
    this.metaKey = `${this.prefix}meta`;
    this.prefsKey = `${this.prefix}prefs`;
    this.storage = options.storage || new LocalStorageAdapter();
    this.isAsync = options.storage?.getItem?.constructor?.name === 'AsyncFunction';
    
    // Always use localStorage for preferences/metadata (small, need sync access)
    this.metaStorage = new LocalStorageAdapter();
    
    this.loadPreferences();
    this.loadMetadata();
    
    // Register actions with Actr if available
    if (this.actr) {
      this.registerActions();
    }
  }
  
  /**
   * Register Cachr actions with Actr
   * 
   * ARCHITECTURE NOTE: Cachr uses ACTIONS-ONLY pattern
   * 
   * Why this is different from notifyr/groupr:
   * 1. Ephemeral Cache: Cache data is temporary
   *    - Cached for performance, not persistence
   *    - LRU eviction - old data automatically removed
   *    - Can be cleared at any time
   *    - CRUD would add unnecessary overhead
   * 
   * 2. Storage Abstraction: Multiple storage backends
   *    - LocalStorage, IndexedDB, Memory adapters
   *    - Storage layer handles persistence
   *    - OpCentral doesn't own the storage mechanism
   *    - No need for OpCentral CRUD
   * 
   * 3. High-Frequency Operations: Cache accessed constantly
   *    - Every data fetch checks cache first
   *    - Performance-critical (must be <1ms)
   *    - CRUD overhead would negate caching benefits
   * 
   * Pattern: Actions-Only (No CRUD)
   * - Actions: For cache operations (set, get, clear, prune)
   * - No CRUD: Cache is ephemeral, storage-backed
   * - Adapter-based: Storage layer handles persistence
   * 
   * Compare to other patterns:
   * - clippr: Actions-Only (ephemeral, OS-integrated)
   * - searchr: Actions-Only (ephemeral results, external data)
   * - cachr: Actions-Only (ephemeral cache, storage-backed, LRU)
   * 
   * See: /docs/ACTR_INTEGRATION_PATTERNS.md for complete explanation
   * See: /packages/cachr/README.md "Actr Integration" section
   */
  registerActions(): void {
    if (!this.actr) return;
    
    // set action (high-frequency)
    this.actr.register('cachr:set', {
      handler: async (params) => {
        return await this.set(params.id, params.data, params.metadata);
      },
      description: 'Set cache item'
    });
    
    // get action (high-frequency)
    this.actr.register('cachr:get', {
      handler: async (params) => {
        return await this.get(params.id);
      },
      description: 'Get cache item'
    });
    
    // remove action
    this.actr.register('cachr:remove', {
      handler: async (params) => {
        return await this.remove(params.id);
      },
      description: 'Remove cache item'
    });
    
    // clear action
    this.actr.register('cachr:clear', {
      handler: async (params) => {
        return await this.clear();
      },
      description: 'Clear all cache'
    });
    
    // prune action (LRU eviction)
    this.actr.register('cachr:prune', {
      handler: async (params) => {
        return await this.prune(params.targetSize);
      },
      description: 'Prune cache to target size'
    });
    
    // Note: No CRUD operations - cache is ephemeral with LRU eviction.
    // Storage adapters handle persistence, not Actr database.
  }

  /**
   * Load cache preferences from storage
   */
  loadPreferences(): void {
    try {
      const prefs = this.metaStorage.getItem(this.prefsKey);
      this.preferences = prefs ? JSON.parse(prefs) : {
        maxCacheSize: DEFAULT_CACHE_LIMIT,
        enableCache: true,
        autoCleanup: true
      };
    } catch (e) {
      console.error('Failed to load cache preferences:', e);
      this.preferences = {
        maxCacheSize: DEFAULT_CACHE_LIMIT,
        enableCache: true,
        autoCleanup: true
      };
    }
  }

  /**
   * Save cache preferences to storage
   */
  savePreferences(prefs: any): void {
    this.preferences = { ...this.preferences, ...prefs };
    try {
      this.metaStorage.setItem(this.prefsKey, JSON.stringify(this.preferences));
    } catch (e) {
      console.error('Failed to save cache preferences:', e);
    }
  }

  /**
   * Get current cache preferences
   */
  getPreferences(): any {
    return { ...this.preferences };
  }

  /**
   * Load cache metadata (index of all cached items)
   */
  loadMetadata(): void {
    try {
      const meta = this.metaStorage.getItem(this.metaKey);
      this.metadata = meta ? JSON.parse(meta) : {
        items: {},
        totalSize: 0,
        lastCleanup: Date.now()
      };
    } catch (e) {
      console.error('Failed to load cache metadata:', e);
      this.metadata = {
        items: {},
        totalSize: 0,
        lastCleanup: Date.now()
      };
    }
  }

  /**
   * Save cache metadata to storage
   */
  saveMetadata(): void {
    try {
      this.metaStorage.setItem(this.metaKey, JSON.stringify(this.metadata));
    } catch (e) {
      console.error('Failed to save cache metadata:', e);
    }
  }

  /**
   * Get cache key for an item
   */
  getCacheKey(id: string): string {
    return `${this.prefix}${id}`;
  }

  /**
   * Calculate size of data in bytes
   */
  getDataSize(data: any): number {
    const str = typeof data === 'string' ? data : JSON.stringify(data);
    // Use Blob in browser, Buffer in Node.js, or fallback to string length
    if (typeof Blob !== 'undefined') {
      return new Blob([str]).size;
    } else if (typeof Buffer !== 'undefined') {
      return Buffer.byteLength(str, 'utf8');
    } else {
      return str.length;
    }
  }

  /**
   * Calculate approximate size of data
   */
  calculateSize(data: any): number {
    if (typeof data === 'string') {
      return data.length * 2; // UTF-16 encoding
    }
    try {
      return JSON.stringify(data).length * 2;
    } catch (e) {
      console.warn('Failed to calculate size:', e);
      return 0;
    }
  }

  /**
   * Format bytes for display
   */
  formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  }

  /**
   * Check if cache is enabled
   */
  isEnabled(): boolean {
    return this.preferences.enableCache;
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    const maxSize = this.preferences.maxCacheSize;
    const currentSize = this.metadata.totalSize;
    const percentage = (currentSize / maxSize) * 100;
    const itemCount = Object.keys(this.metadata.items).length;

    return {
      currentSize,
      maxSize,
      percentage: percentage.toFixed(1),
      itemCount,
      formattedCurrent: this.formatBytes(currentSize),
      formattedMax: this.formatBytes(maxSize),
      isNearLimit: percentage >= WARNING_THRESHOLD * 100,
      needsPruning: percentage >= 100
    };
  }

  /**
   * Set an item in the cache
   */
  async set(id: string, data: any, metadata: any = {}, _retryCount = 0): Promise<SetResult> {
    if (!this.isEnabled()) {
      return { 
        success: false, 
        reason: 'Cache is disabled'
      };
    }

    // Prevent infinite recursion
    if (_retryCount > 3) {
      console.error('Cache set failed after 3 retry attempts');
      return {
        success: false,
        reason: 'Failed to cache after multiple pruning attempts'
      };
    }

    const key = this.getCacheKey(id);
    const size = this.calculateSize(data);

    // Check if we need to prune before adding
    const stats = this.getStats();
    const newSize = stats.currentSize + size;
    
    if (newSize > this.preferences.maxCacheSize) {
      // Try to prune to make space
      const targetSize = this.preferences.maxCacheSize * PRUNE_TARGET;
      const spaceNeeded = newSize - targetSize;
      
      if (this.preferences.autoCleanup) {
        this.pruneCache(spaceNeeded);
      } else {
        return { 
          success: false, 
          reason: 'Cache limit exceeded and auto-cleanup is disabled',
          stats: this.getStats()
        };
      }
    }

    try {
      // Store the data (await if async)
      const setResult = this.storage.setItem(key, typeof data === 'string' ? data : JSON.stringify(data));
      if (setResult instanceof Promise) {
        await setResult;
      }

      // Update metadata
      this.metadata.items[id] = {
        id,
        size,
        lastAccessed: Date.now(),
        created: this.metadata.items[id]?.created || Date.now(),
        ...metadata
      };

      // Update total size
      const oldSize = this.metadata.items[id]?.size || 0;
      this.metadata.totalSize = this.metadata.totalSize - oldSize + size;

      this.saveMetadata();

      return { 
        success: true, 
        size,
        stats: this.getStats()
      };
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.warn(`Storage quota exceeded (attempt ${_retryCount + 1}/3), auto-pruning oldest items...`);
        
        // Prune 25% of items
        const itemCount = Object.keys(this.metadata.items).length;
        
        if (itemCount === 0) {
          // No items to prune, item is too large for storage
          console.error('Cannot cache item: exceeds browser storage quota');
          return {
            success: false,
            reason: 'Item too large for browser storage quota'
          };
        }
        
        const pruneCount = Math.max(1, Math.floor(itemCount * 0.25));
        
        // Get oldest items
        const items = Object.entries(this.metadata.items)
          .sort((a, b) => a[1].lastAccessed - b[1].lastAccessed)
          .slice(0, pruneCount);
        
        // Remove them
        items.forEach(([itemKey]) => this.remove(itemKey));
        
        console.log(`Pruned ${pruneCount} items, retrying...`);
        
        // Try again with incremented retry count
        return await this.set(id, data, metadata, _retryCount + 1);
      }
      
      console.error('Failed to cache item:', e);
      return { 
        success: false, 
        reason: e.message,
        error: e
      };
    }
  }

  /**
   * Get an item from the cache
   */
  get(id: string, parseJson = true): any {
    if (!this.isEnabled()) {
      return null;
    }

    const key = this.getCacheKey(id);
    
    try {
      const data = this.storage.getItem(key);
      
      if (data === null) {
        return null;
      }

      // Update last accessed time
      if (this.metadata.items[id]) {
        this.metadata.items[id].lastAccessed = Date.now();
        this.saveMetadata();
      }

      return parseJson ? JSON.parse(data) : data;
    } catch (e) {
      console.error('Failed to retrieve cached item:', e);
      return null;
    }
  }

  /**
   * Check if an item exists in cache
   */
  has(id: string): boolean {
    return this.metadata.items[id] !== undefined;
  }

  /**
   * Remove an item from cache
   */
  remove(id: string): { success: boolean; error?: any } {
    const key = this.getCacheKey(id);
    
    try {
      this.storage.removeItem(key);
      
      if (this.metadata.items[id]) {
        this.metadata.totalSize -= this.metadata.items[id].size;
        delete this.metadata.items[id];
        this.saveMetadata();
      }

      return { success: true };
    } catch (e) {
      console.error('Failed to remove cached item:', e);
      return { success: false, error: e };
    }
  }

  /**
   * Prune cache using LRU strategy
   */
  pruneCache(targetBytes: number | null = null): { removed: number; freedSpace: number; formattedFreed: string; remainingItems: number; stats: CacheStats } {
    const stats = this.getStats();
    const target = targetBytes || (this.preferences.maxCacheSize * (1 - PRUNE_TARGET));

    // Sort items by last accessed (oldest first)
    const items = Object.values(this.metadata.items)
      .sort((a, b) => a.lastAccessed - b.lastAccessed);

    let freedSpace = 0;
    const removed = [];

    for (const item of items) {
      if (freedSpace >= target) break;

      this.remove(item.id);
      freedSpace += item.size;
      removed.push(item.id);
    }

    this.metadata.lastCleanup = Date.now();
    this.saveMetadata();

    return {
      removed: removed.length,
      freedSpace,
      formattedFreed: this.formatBytes(freedSpace),
      remainingItems: Object.keys(this.metadata.items).length,
      stats: this.getStats()
    };
  }

  /**
   * Clear all cache
   */
  clearAll(): { removed: number; stats: CacheStats } {
    const items = Object.keys(this.metadata.items);
    
    items.forEach(id => {
      const key = this.getCacheKey(id);
      this.storage.removeItem(key);
    });

    this.metadata = {
      items: {},
      totalSize: 0,
      lastCleanup: Date.now()
    };

    this.saveMetadata();

    return {
      removed: items.length,
      stats: this.getStats()
    };
  }

  /**
   * Get list of all cached items
   */
  listItems(): any[] {
    return Object.values(this.metadata.items)
      .sort((a, b) => b.lastAccessed - a.lastAccessed)
      .map(item => ({
        ...item,
        formattedSize: this.formatBytes(item.size),
        age: Date.now() - item.created,
        lastAccessedAgo: Date.now() - item.lastAccessed
      }));
  }

  /**
   * Override cache temporarily (bypass size limits)
   */
  forceSet(id: string, data: any, metadata: any = {}): Promise<SetResult> {
    const originalAutoCleanup = this.preferences.autoCleanup;
    this.preferences.autoCleanup = false;

    const result = this.set(id, data, { ...metadata, forced: true });

    this.preferences.autoCleanup = originalAutoCleanup;

    return result;
  }
}

// Singleton instance with default options
let defaultInstance: CachrManager | null = null;

/**
 * Get singleton instance of CachrManager
 * @param {Object} options - Configuration options
 * @returns {CachrManager}
 */
export const getCachr = (options: CachrOptions = {}): CachrManager => {
  if (!defaultInstance) {
    defaultInstance = new CachrManager(options);
  }
  return defaultInstance;
};

/**
 * Reset singleton instance (useful for testing)
 */
export const resetCachr = () => {
  defaultInstance = null;
};
